import { ChevronDown, Lock, LogOut, Maximize, Menu, Minimize, PanelLeftClose } from 'lucide-react';
import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Link, useNavigate } from 'react-router-dom';
import { NotificationSound } from '../CommonComponents/CommonNotification';
import { ApiCall } from '../CommonComponents/CustomHooks';
import { CommonApiData, deleteCookie } from '../CommonComponents/EnvConfig';
import { GetSession, RemoveSession } from '../CommonComponents/criptoUtils';
import { setOpen } from '../Redux/Services/headerSlice';

export const toggleFullScreen = (event, ID, setIsFullScreen, isFullScreen) => {
    if (!isFullScreen) {
        let element = document.documentElement;
        if (ID !== undefined && ID !== '') {
            element = document.getElementById(ID);
        }
        if (element) {
            if (element.requestFullscreen) {
                element.requestFullscreen();
            } else if (element.mozRequestFullScreen) {
                element.mozRequestFullScreen();
            } else if (element.webkitRequestFullscreen) {
                element.webkitRequestFullscreen();
            } else if (element.msRequestFullscreen) {
                element.msRequestFullscreen();
            }
            element.style.overflow = 'auto';
            setIsFullScreen(true);
        }
    } else {
        if (document.exitFullscreen) {
            document.exitFullscreen();
        } else if (document.mozCancelFullScreen) {
            document.mozCancelFullScreen();
        } else if (document.webkitExitFullscreen) {
            document.webkitExitFullscreen();
        } else if (document.msExitFullscreen) {
            document.msExitFullscreen();
        }
        document.body.style.overflow = 'auto';
        setIsFullScreen(false);
    }
};

const Header = () => {
    const dispatch = useDispatch();
    const open = useSelector((state) => state.header.open);
    const [isFullScreen, setIsFullScreen] = useState(false);
    const [isDropdown, setIsDropdown] = useState(false);
    const userprofile = GetSession('localstorage', 'profile');
    const navigate = useNavigate();
    const user = GetSession('cookies', 'user');
    const data = CommonApiData();

    const Logout = async () => {
        let data = await ApiCall('/api/UserLogin/Logout', {});
        if (data.status === 'SUCCESS') {
            deleteCookie('user');
            RemoveSession("cookies", 'user')
            NotificationSound(data.status, data.message);
            navigate('/');
        }
    };

    useEffect(() => {
        document.body.classList.add('light');
    }, []);


    useEffect(() => {
        if (!user?.loginToken) {
            NotificationSound('EXPIRED', 'EXPIRED');
        }
    }, [user]);

    return (
        <nav className="fixed top-0 left-0 right-0 bg-gradient-to-r from-blueled-light via-blue-400 to-blue-600 shadow-md z-50">
            <div className="flex items-center justify-between px-4 h-16">
                {/* Left Section - Logo & Menu Toggle */}
                <div className="flex items-center">
                    <div className="flex items-center gap-3">
                        <img
                            src={`./main-logo.png`}
                            className="h-12 object-contain"
                            alt="Main logo"
                        />
                    </div>
                    <button
                        onClick={() => dispatch(setOpen(!open))}
                        className="p-2 hover:bg-white/10 rounded-lg transition-colors ml-4"
                    >
                        {open ? <PanelLeftClose size={24} className="text-white" /> : <Menu size={24} className="text-white" />}
                    </button>
                </div>

                {/* Right Section - User Menu & Fullscreen */}
                <div className="flex items-center gap-3">
                    {/* User Dropdown */}
                    <div className="relative">
                        <button
                            onClick={() => setIsDropdown(!isDropdown)}
                            className="flex items-center gap-3 px-4 py-2 hover:bg-white/10 rounded-lg transition-all"
                        >
                            <div className="text-right hidden sm:block">
                                <p className="text-sm font-semibold text-white flex items-center gap-2">
                                    <span className="w-2 h-2 bg-green-400 rounded-full animate-pulse"></span>
                                    {data?.UserCode ? `${data?.UserName}` : '----'}
                                </p>
                            </div>
                            <img
                                src={userprofile ? userprofile : "./images/avatar.png"}
                                alt="user"
                                className="w-10 h-10 rounded-full border-2 border-white/50 object-cover"
                                onError={(e) => {
                                    e.target.src = `https://ui-avatars.com/api/?name=${data?.UserName || "----"}&background=30667d&color=fff`;
                                }}
                            />
                            <ChevronDown
                                size={16}
                                className={`text-white transition-transform duration-200 ${isDropdown ? 'rotate-180' : ''}`}
                            />
                        </button>

                        {/* Dropdown Menu */}
                        {isDropdown && (
                            <>
                                <div
                                    className="fixed inset-0 z-40"
                                    onClick={() => setIsDropdown(false)}
                                ></div>
                                <div className="absolute right-0 mt-2 w-56 bg-white rounded-xl shadow-xl border border-gray-200 py-2 z-50">
                                    <div className="px-4 py-3 border-b border-gray-100">
                                        <p className="text-sm font-semibold text-gray-800">
                                            {data?.UserName || ''}
                                        </p>
                                        <p className="text-xs text-gray-500">
                                            {data?.UserCode || '----'}
                                        </p>
                                    </div>
                                    <Link
                                        to="/User/ChangePassword"
                                        className="flex items-center gap-3 px-4 py-2.5 hover:bg-gray-50 transition-colors"
                                        onClick={() => setIsDropdown(false)}
                                    >
                                        <Lock size={18} className="text-blueled" />
                                        <span className="text-sm text-gray-700">Change Password</span>
                                    </Link>
                                    <button
                                        onClick={() => {
                                            Logout();
                                            setIsDropdown(false);
                                        }}
                                        className="flex items-center gap-3 px-4 py-2.5 hover:bg-gray-50 transition-colors w-full text-left"
                                    >
                                        <LogOut size={18} className="text-red-500" />
                                        <span className="text-sm text-gray-700">Logout</span>
                                    </button>
                                </div>
                            </>
                        )}
                    </div>

                    {/* Fullscreen Toggle */}
                    <button
                        onClick={(event) => toggleFullScreen(event, '', setIsFullScreen, isFullScreen)}
                        className="p-2.5 hover:bg-white/10 rounded-lg transition-colors"
                        title="Toggle Fullscreen"
                    >
                        {!isFullScreen ? (
                            <Maximize size={22} className="text-white" />
                        ) : (
                            <Minimize size={22} className="text-white" />
                        )}
                    </button>
                </div>
            </div>
        </nav>
    );
};

export default Header;